// A/B test: CommunityHome "Demo" button (variant A = shown, B = hidden).
// The visitor ID and variant both live in localStorage so the same browser
// keeps seeing the same variant on every visit, and the signup flow can
// later attribute a conversion back to the same anonymous visitor.

const VISITOR_ID_KEY = 'ic_visitor_id';
const DEMO_BUTTON_VARIANT_KEY = 'ic_ab_demo_button_variant';
const DEMO_BUTTON_TEST_NAME = 'community_home_demo_button';

// A link carrying ?vid= (from the "open in your browser" sheet in Facebook /
// Instagram) hands this browser the visitor ID from the in-app browser, so the
// visit before and after the switch shows as one person in analytics. Read
// once per page load, then dropped from the address bar.
const VID_RE = /^[A-Za-z0-9_-]{8,64}$/;
let vidChecked = false;
const adoptVisitorIdFromUrl = () => {
  if (vidChecked || typeof window === 'undefined') return;
  vidChecked = true;
  try {
    const url = new URL(window.location.href);
    const vid = url.searchParams.get('vid');
    if (!vid) return;
    if (VID_RE.test(vid)) localStorage.setItem(VISITOR_ID_KEY, vid);
    url.searchParams.delete('vid');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch { /* ignore */ }
};

// `url` with this visitor's ID added as ?vid=.
export const withVisitorId = (url) => {
  try {
    const u = new URL(url, window.location.href);
    u.searchParams.set('vid', getVisitorId());
    return u.toString();
  } catch {
    return url;
  }
};

export const getVisitorId = () => {
  adoptVisitorIdFromUrl();
  let id = localStorage.getItem(VISITOR_ID_KEY);
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || `v_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(VISITOR_ID_KEY, id);
  }
  return id;
};

// Deterministic per-visitor 50/50 split, assigned once and reused forever.
// ?demo=1 / ?demo=0 show / hide the Demo button to check (not logged, not saved).
export const demoButtonForced = () => {
  try {
    const v = new URLSearchParams(window.location.search).get('demo');
    return v === '1' ? 'A' : v === '0' ? 'B' : null;
  } catch { return null; }
};

export const getDemoButtonVariant = () => {
  const forced = demoButtonForced();
  if (forced) return forced;
  let variant = localStorage.getItem(DEMO_BUTTON_VARIANT_KEY);
  if (variant !== 'A' && variant !== 'B') {
    variant = Math.random() < 0.5 ? 'A' : 'B';
    localStorage.setItem(DEMO_BUTTON_VARIANT_KEY, variant);
  }
  return variant;
};

// Fire-and-forget exposure log, deduped per community per browser session so
// re-renders/navigations within the same visit don't spam the endpoint (the
// backend also dedupes via an upsert, this just avoids the extra requests).
export const logDemoButtonExposure = (communityId, variant) => {
  if (!communityId) return;
  const dedupeKey = `ic_ab_exposed_${DEMO_BUTTON_TEST_NAME}_${communityId}`;
  if (sessionStorage.getItem(dedupeKey)) return;
  sessionStorage.setItem(dedupeKey, '1');

  const apiBase = process.env.REACT_APP_API_BASE_URL;
  if (!apiBase) return;

  fetch(`${apiBase}/ab-test/expose`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      visitorId: getVisitorId(),
      variant,
      communityId,
      testName: DEMO_BUTTON_TEST_NAME,
    }),
  }).catch(() => {}); // best-effort — never block or throw on the page
};

// A/B test: community home hero flow (community.heroFlowABTest).
// A = the normal hero (tagline + Join); the other half get `arm`: B = the
// quick-match hero, C = quick-match opening on a "join now, or look first"
// choice. Assigned once per browser per community (the half is kept when
// the arm changes) and logged as 'community_hero_flow', so signups can be
// compared (exposure.converted).
const HERO_FLOW_TEST_NAME = 'community_hero_flow';

export const getHeroFlowVariant = (communityId, arm = 'B') => {
  const key = `ic_ab_hero_flow_${communityId}`;
  let half = null;
  try { half = localStorage.getItem(key); } catch { /* storage blocked */ }
  if (half !== 'A' && half !== 'B') {
    half = Math.random() < 0.5 ? 'A' : 'B';
    try { localStorage.setItem(key, half); } catch { /* ignore */ }
  }
  return half === 'A' ? 'A' : arm;
};

export const logHeroFlowExposure = (communityId, variant) => {
  if (!communityId) return;
  const dedupeKey = `ic_ab_exposed_${HERO_FLOW_TEST_NAME}_${communityId}`;
  try {
    if (sessionStorage.getItem(dedupeKey)) return;
    sessionStorage.setItem(dedupeKey, '1');
  } catch { /* ignore */ }
  const apiBase = process.env.REACT_APP_API_BASE_URL;
  if (!apiBase) return;
  fetch(`${apiBase}/ab-test/expose`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ visitorId: getVisitorId(), variant, communityId, testName: HERO_FLOW_TEST_NAME }),
  }).catch(() => {});
};

// A/B test: the sign-up page (/groupsearch/join/:id) with the group's
// description (A) or without it and a bigger "Free…" line (B). Half each,
// kept per browser per community; ?blurb=1 / ?blurb=0 shows A / B (for
// checking, not logged). Logged as 'signup_page_blurb' — a signup marks it
// converted like the other tests.
const SIGNUP_BLURB_TEST_NAME = 'signup_page_blurb';

export const getSignupBlurbVariant = (communityId) => {
  try {
    const forced = new URLSearchParams(window.location.search).get('blurb');
    if (forced === '1') return { variant: 'A', forced: true };
    if (forced === '0') return { variant: 'B', forced: true };
  } catch { /* ignore */ }
  const key = `ic_ab_signup_blurb_${communityId}`;
  let v = null;
  try { v = localStorage.getItem(key); } catch { /* storage blocked */ }
  if (v !== 'A' && v !== 'B') {
    v = Math.random() < 0.5 ? 'A' : 'B';
    try { localStorage.setItem(key, v); } catch { /* ignore */ }
  }
  return { variant: v, forced: false };
};

export const logSignupBlurbExposure = (communityId, variant) => {
  if (!communityId) return;
  const dedupeKey = `ic_ab_exposed_${SIGNUP_BLURB_TEST_NAME}_${communityId}`;
  try {
    if (sessionStorage.getItem(dedupeKey)) return;
    sessionStorage.setItem(dedupeKey, '1');
  } catch { /* ignore */ }
  const apiBase = process.env.REACT_APP_API_BASE_URL;
  if (!apiBase) return;
  fetch(`${apiBase}/ab-test/expose`, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify({ visitorId: getVisitorId(), variant, communityId, testName: SIGNUP_BLURB_TEST_NAME }),
    keepalive: true,
  }).catch(() => {});
};

// "Watch the video" (community.aboutVideo) on the community page, when the
// community has aboutVideoABTest on: A = no video, B = the button over the
// hero, C = the video's still, in a hexagon, under the title and tagline
// (pages/CommunityHome.js). A third each for new visitors, kept per browser per community
// (earlier visitors keep their A or B); ?video=1 / ?video=0 / ?video=2 show
// B / A / C (for checking, not logged). Logged as 'community_about_video' — a signup
// marks it converted like the other tests.
const ABOUT_VIDEO_TEST_NAME = 'community_about_video';

export const getAboutVideoVariant = (communityId) => {
  try {
    const forced = new URLSearchParams(window.location.search).get('video');
    if (forced === '1') return { variant: 'B', forced: true };
    if (forced === '0') return { variant: 'A', forced: true };
    if (forced === '2') return { variant: 'C', forced: true };
  } catch { /* ignore */ }
  const key = `ic_ab_about_video_${communityId}`;
  let v = null;
  try { v = localStorage.getItem(key); } catch { /* storage blocked */ }
  if (!['A', 'B', 'C'].includes(v)) {
    v = ['A', 'B', 'C'][Math.floor(Math.random() * 3)];
    try { localStorage.setItem(key, v); } catch { /* ignore */ }
  }
  return { variant: v, forced: false };
};

export const logAboutVideoExposure = (communityId, variant) => {
  if (!communityId) return;
  const dedupeKey = `ic_ab_exposed_${ABOUT_VIDEO_TEST_NAME}_${communityId}`;
  try {
    if (sessionStorage.getItem(dedupeKey)) return;
    sessionStorage.setItem(dedupeKey, '1');
  } catch { /* ignore */ }
  const apiBase = process.env.REACT_APP_API_BASE_URL;
  if (!apiBase) return;
  fetch(`${apiBase}/ab-test/expose`, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify({ visitorId: getVisitorId(), variant, communityId, testName: ABOUT_VIDEO_TEST_NAME }),
    keepalive: true,
  }).catch(() => {});
};

// Where Join goes on the community page (community.joinChatABTest), for
// visitors who aren't logged in: A = the sign-up form (/groupsearch/join),
// B = the chat sign-up (/meet/<ref>: questions first, then the account).
// Half each, kept per browser per community; ?join=chat / ?join=form force
// one (for checking, not logged). Logged as 'community_join_route' — a
// signup (from either route) marks it converted like the other tests.
const JOIN_ROUTE_TEST_NAME = 'community_join_route';

export const getJoinRouteVariant = (communityId) => {
  try {
    const forced = new URLSearchParams(window.location.search).get('join');
    if (forced === 'chat') return { variant: 'B', forced: true };
    if (forced === 'form') return { variant: 'A', forced: true };
  } catch { /* ignore */ }
  const key = `ic_ab_join_route_${communityId}`;
  let v = null;
  try { v = localStorage.getItem(key); } catch { /* storage blocked */ }
  if (v !== 'A' && v !== 'B') {
    v = Math.random() < 0.5 ? 'A' : 'B';
    try { localStorage.setItem(key, v); } catch { /* ignore */ }
  }
  return { variant: v, forced: false };
};

export const logJoinRouteExposure = (communityId, variant) => {
  if (!communityId) return;
  const dedupeKey = `ic_ab_exposed_${JOIN_ROUTE_TEST_NAME}_${communityId}`;
  try {
    if (sessionStorage.getItem(dedupeKey)) return;
    sessionStorage.setItem(dedupeKey, '1');
  } catch { /* ignore */ }
  const apiBase = process.env.REACT_APP_API_BASE_URL;
  if (!apiBase) return;
  fetch(`${apiBase}/ab-test/expose`, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify({ visitorId: getVisitorId(), variant, communityId, testName: JOIN_ROUTE_TEST_NAME }),
    keepalive: true,
  }).catch(() => {});
};
